<template>
  <div>
    <div class="hero has-text-centered">
      <h1 class="is-big title">
        <p>{{ title }}</p>
      </h1>
      <b-button @click="printPage" class="no-print" type="is-primary">
        <b-icon pack="fas" icon="print"></b-icon>
      </b-button>
    </div>
    <div class="no-print">
      <b-collapse
        v-if="showConflicts"
        class="card"
        animation="slide"
        :model-value="isOpen === 'conflicts'"
        @update:modelValue="onCollapseUpdate('conflicts', $event)"
      >
        <template #trigger="props">
          <div class="card-header" role="button">
            <p class="card-header-title">{{ $t('ConflictedRevenueHeader') }}</p>
            <a class="card-header-icon">
              <b-icon pack="fas" :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
            </a>
          </div>
        </template>
        <div class="card-content">
          <conflictList :conflicts="shoppingCentersList || []" />
        </div>
      </b-collapse>
      <b-collapse
        v-if="showMissing"
        class="card"
        animation="slide"
        :model-value="isOpen === 'missingRevenues'"
        @update:modelValue="onCollapseUpdate('missingRevenues', $event)"
      >
        <template #trigger="props">
          <div class="card-header" role="button">
            <p class="card-header-title">{{ $t('MissingRevenueHeader') }}</p>
            <a class="card-header-icon">
              <b-icon pack="fas" :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
            </a>
          </div>
        </template>
        <div class="card-content">
          <missingRevenuesList :missingRevenues="shoppingCentersList || []" />
        </div>
      </b-collapse>
      <b-collapse
        v-if="showSucceed"
        class="card"
        animation="slide"
        :model-value="isOpen === 'succeed'"
        @update:modelValue="onCollapseUpdate('succeed', $event)"
      >
        <template #trigger="props">
          <div class="card-header" role="button">
            <p class="card-header-title">{{ $t('SuccessRevenueHeader') }}</p>
            <a class="card-header-icon">
              <b-icon pack="fas" :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
            </a>
          </div>
        </template>
        <div class="card-content">
          <successList :shops="shoppingCentersList || []" />
        </div>
      </b-collapse>
      <footer class="hero no-print">
        <b-button type="is-primary" @click="closeResponseModal">{{ $t('Close') }}</b-button>
      </footer>
    </div>
    <div class="print">
      <div v-if="showConflicts">
        <h1 class="title">{{ $t('ConflictedRevenueHeader') }}</h1>
        <conflictList :conflicts="shoppingCentersList || []" />
      </div>
      <div v-if="showMissing">
        <h1 class="title">{{ $t('MissingRevenueHeader') }}</h1>
        <missingRevenuesList :missingRevenues="shoppingCentersList || []" />
      </div>
      <div v-if="showSucceed">
        <h1 class="title">{{ $t('SuccessRevenueHeader') }}</h1>
        <successList :shops="shoppingCentersList || []" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import type { Section, Shop } from '@/stores/app';
import conflictList from '@/components/revenueResponse/ConflictList.vue';
import successList from '@/components/revenueResponse/SuccessList.vue';
import missingRevenuesList from '@/components/revenueResponse/MissingRevenues.vue';
import {
  resolveDefaultOpenState,
  type RevenueResponseDefaultOpenState,
} from '@/components/revenueResponse/defaultOpenResolver';
import revenueService from '@/services/revenueService';

type RevenueResponseShop = Shop & { sections?: Section[] };
type RevenueResponseOpenState =
  | RevenueResponseDefaultOpenState
  | 'missingRevenues';

interface RevenueResponseHistoryState {
  shoppingCentersList?: string;
  date?: string;
}

export default defineComponent({
  components: {
    conflictList,
    successList,
    missingRevenuesList,
  },
  computed: {
    title(): string {
      const monthNames = this.$tm('MonthNames');
      const safeMonthNames = Array.isArray(monthNames) ? monthNames : [];
      const revenueMonth = this.date
        ? safeMonthNames[this.date.getMonth()] ?? ''
        : '';

      return `${this.$t('RevenueResponseSummaryHeader')} ${revenueMonth}`;
    },
    showConflicts(): boolean {
      if (!this.shoppingCentersList) return false;

      return this.shoppingCentersList.some((shop: RevenueResponseShop) =>
        (shop.sections ?? []).some((section: Section) => section.updateSucceed === false)
      );
    },
    showMissing(): boolean {
      if (!this.shoppingCentersList) return false;

      return this.shoppingCentersList.some((shop: RevenueResponseShop) =>
        (shop.sections ?? []).some(
          (section: Section) =>
            revenueService.revenueExistsForShop(shop.shopId, section.sectionId) === false &&
            section.updateSucceed !== true
        )
      );
    },
    showSucceed(): boolean {
      if (!this.shoppingCentersList) return false;

      return this.shoppingCentersList.some((shop: RevenueResponseShop) =>
        (shop.sections ?? []).some((section: Section) => section.updateSucceed === true)
      );
    },
  },
  methods: {
    onCollapseUpdate(
      panel: 'conflicts' | 'missingRevenues' | 'succeed',
      isExpanded: boolean
    ): void {
      if (isExpanded) {
        this.isOpen = panel;
        return;
      }

      if (this.isOpen === panel) {
        this.isOpen = '';
      }
    },
    closeResponseModal(): void {
      revenueService.postRevenueDone([]);
      this.$router.push('/shops');
    },
    printPage(): void {
      window.print();
    },
  },
  data() {
    return {
      isOpen: '' as RevenueResponseOpenState,
      shoppingCentersList: null as RevenueResponseShop[] | null,
      date: null as Date | null,
    };
  },
  mounted() {
    // Read data from router state (Vue Router 4 pattern)
    const state = window.history.state as RevenueResponseHistoryState | null;

    if (state && state.shoppingCentersList) {
      this.shoppingCentersList = JSON.parse(state.shoppingCentersList) as RevenueResponseShop[];
      const parsedDate = state.date ? new Date(state.date) : null;
      this.date = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
    } else {
      // No data provided, redirect back
      this.$router.push({ name: 'shopRevenue' });
      return;
    }
    this.isOpen = resolveDefaultOpenState({
      hasConflicts: this.showConflicts,
      hasSuccess: this.showSucceed,
    });
  },
});
</script>
<style>
@media print {
  .no-print,
  .no-print * {
    display: none !important;
  }
  .print {
    display: block !important;
  }
}

.print {
  display: none;
}
</style>
